import React from 'react'
import PropTypes from 'prop-types'
import Dropzone from 'react-dropzone'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap'
import ListItem from './FileList'
import './FileUploadPopup.scss'
import FormattedMessage from '../../FormattedMessage/FormattedMessage'
import './FileUploadInput.scss'
import RedCloseIcon from './FileUploadIcon/RedCloseIcon'

const maxSize = 10 * 1024 * 1024
const fileLimit = 5

class FileuploadInput extends React.Component {
  constructor(props) {
    super(props)
    this.removeAll = this.removeAll.bind(this)
    this.onDrop = this.onDrop.bind(this)
    this.togglePopupRemoveAll = this.togglePopupRemoveAll.bind(this)
    this.togglePopupRemoveAllClose = this.togglePopupRemoveAllClose.bind(this)
    this.togglePopupSize = this.togglePopupSize.bind(this)
    this.togglePopupSizeClose = this.togglePopupSizeClose.bind(this)
    this.togglePopupType = this.togglePopupType.bind(this)
    this.togglePopupTypeClose = this.togglePopupTypeClose.bind(this)
    this.togglePopupLimit = this.togglePopupLimit.bind(this)
    this.togglePopupLimitClose = this.togglePopupLimitClose.bind(this)
    this.removeItem = this.removeItem.bind(this)
    this.state = {
      showPopupType: false,
      showPopupSize: false,
      showPopupLimit: false,
      showPopupRemoveAll: false,
    }
  }

  onDrop(file) {
    const fileType = file[0].type
    const fileSize = file[0].size
    const fileName = file[0].name
    const { onChange, value } = this.props

    if (fileSize > maxSize) {
      return this.setState({
        showPopupSize: true,
      })
    }

    if (
      fileType === 'image/png' ||
      fileType === 'image/jpeg' ||
      fileType === 'image/bmp' ||
      fileType === 'application/pdf'
    ) {
      if (value.some(el => el.name === fileName)) {
        return
      }

      const newValue = value.concat(file)
      if (newValue.length > fileLimit) {
        onChange(newValue.slice(0, fileLimit))
        this.setState({
          showPopupLimit: true,
        })
      } else {
        onChange(newValue)
      }
    } else {
      this.setState({
        showPopupType: true,
      })
    }
  }

  removeAll() {
    const { onChange } = this.props
    onChange([])
    this.setState({
      showPopupRemoveAll: false,
    })
  }

  removeItem(file) {
    const { onChange, value } = this.props
    onChange(value.filter(item => item !== file))
  }

  togglePopupRemoveAll() {
    this.setState({
      showPopupRemoveAll: true,
    })
  }

  togglePopupRemoveAllClose() {
    this.setState({
      showPopupRemoveAll: false,
    })
  }

  togglePopupSize() {
    this.setState({
      showPopupSize: true,
    })
  }

  togglePopupSizeClose() {
    this.setState({
      showPopupSize: false,
    })
  }

  togglePopupLimit() {
    this.setState({
      showPopupLimit: true,
    })
  }

  togglePopupLimitClose() {
    this.setState({
      showPopupLimit: false,
    })
  }

  togglePopupType() {
    this.setState({
      showPopupType: true,
    })
  }

  togglePopupTypeClose() {
    this.setState({
      showPopupType: false,
    })
  }

  render() {
    const {
      value,
      id,
      name,
      required,
      'aria-required': ariaRequired,
      'aria-invalid': ariaInvalid,
      'aria-describedby': ariaDescribedBy,
      label,
    } = this.props

    const fieldId = id || (name ? `file-upload-${name}` : 'file-upload')
    const isRequired = required || ariaRequired
    const files = value.map((file, index) => (
      <ListItem
        key={file.name}
        value={file.name}
        removeItem={() => this.removeItem(file)}
        index={index}
      />
    ))

    const { showPopupRemoveAll, showPopupSize, showPopupType, showPopupLimit } = this.state

    // Create unique IDs for modals for proper ARIA attributes
    const removeAllModalId = `${fieldId}-remove-all-modal`
    const sizeModalId = `${fieldId}-size-modal`
    const typeModalId = `${fieldId}-type-modal`
    const limitModalId = `${fieldId}-limit-modal`

    return (
      <div
        className="file-upload-container"
        aria-labelledby={label ? `${fieldId}-label` : undefined}
      >
        {label && (
          <label id={`${fieldId}-label`} htmlFor={fieldId}>
            <FormattedMessage id={label} />
            {isRequired && (
              <span className="required-indicator" aria-hidden="true">
                *
              </span>
            )}
            {isRequired && <span className="sr-only">(required)</span>}
          </label>
        )}

        <Dropzone onDrop={this.onDrop} multiple={false} noDrag>
          {({ getRootProps, getInputProps }) => (
            <section className="container-dropzone">
              <div
                {...getRootProps({
                  className: 'dropzone',
                  id: fieldId,
                  'aria-required': isRequired,
                  'aria-invalid': ariaInvalid,
                  'aria-describedby': ariaDescribedBy,
                })}
                role="button"
                tabIndex={0}
              >
                <input
                  {...getInputProps()}
                  accept="image/png, image/jpeg, image/bmp, application/pdf"
                />
                <div className="upload-button-text">
                  <FormattedMessage id="global.label.addAttachment" />
                </div>
              </div>
              <div className="info-size" aria-live="polite">
                <FormattedMessage id="global.label.attachmentFileLimit" />
              </div>
              <aside>
                {files.length === 0 ? (
                  <div className="empty-file-container" aria-live="polite">
                    <p>
                      <FormattedMessage id="global.label.attachmentNoFilesAttached" />
                    </p>
                  </div>
                ) : (
                  <div className="files-container" aria-live="polite">
                    <button
                      type="button"
                      className="title-container"
                      onClick={this.togglePopupRemoveAll}
                      aria-label="Remove all files"
                    >
                      <div className="redCloseIcon" aria-hidden="true">
                        <RedCloseIcon />
                      </div>
                      <div>
                        <FormattedMessage id="global.label.attachmentRemoveAll" />
                      </div>
                    </button>
                    <ul aria-label="Attached files list">{files}</ul>
                  </div>
                )}
              </aside>

              <Modal
                isOpen={showPopupRemoveAll}
                centered
                backdrop
                toggle={this.togglePopupRemoveAllClose}
                aria-labelledby={`${removeAllModalId}-title`}
                aria-describedby={`${removeAllModalId}-desc`}
              >
                <ModalHeader
                  toggle={this.togglePopupRemoveAllClose}
                  id={`${removeAllModalId}-title`}
                >
                  <span className="sr-only">Confirm Remove All Files</span>
                </ModalHeader>
                <ModalBody>
                  <p id={`${removeAllModalId}-desc`} className="text-center">
                    <FormattedMessage id="global.label.attachmentRemoveAllConfirm" />
                  </p>
                </ModalBody>
                <ModalFooter>
                  <Button outline color="secondary" onClick={this.togglePopupRemoveAllClose}>
                    <FormattedMessage id="global.label.cancel" />
                  </Button>
                  <Button color="danger" onClick={this.removeAll}>
                    <FormattedMessage id="global.label.attachmentRemoveFiles" />
                  </Button>
                </ModalFooter>
              </Modal>

              <Modal
                isOpen={showPopupSize}
                centered
                backdrop
                toggle={this.togglePopupSizeClose}
                aria-labelledby={`${sizeModalId}-title`}
                aria-describedby={`${sizeModalId}-desc`}
              >
                <ModalHeader toggle={this.togglePopupSizeClose} id={`${sizeModalId}-title`}>
                  <span className="sr-only">File Size Error</span>
                </ModalHeader>
                <ModalBody>
                  <p id={`${sizeModalId}-desc`} className="text-center text-danger">
                    <FormattedMessage id="global.label.attachmentLimitFileSize" />
                  </p>
                </ModalBody>
                <ModalFooter>
                  <Button outline color="danger" onClick={this.togglePopupSizeClose}>
                    <FormattedMessage id="global.label.popoverClose" />
                  </Button>
                </ModalFooter>
              </Modal>

              <Modal
                isOpen={showPopupType}
                centered
                backdrop
                toggle={this.togglePopupTypeClose}
                aria-labelledby={`${typeModalId}-title`}
                aria-describedby={`${typeModalId}-desc`}
              >
                <ModalHeader toggle={this.togglePopupTypeClose} id={`${typeModalId}-title`}>
                  <span className="sr-only">File Type Error</span>
                </ModalHeader>
                <ModalBody>
                  <p id={`${typeModalId}-desc`} className="text-center text-danger">
                    <FormattedMessage id="global.label.attachmentFileType" />
                  </p>
                </ModalBody>
                <ModalFooter>
                  <Button outline color="danger" onClick={this.togglePopupTypeClose}>
                    <FormattedMessage id="global.label.popoverClose" />
                  </Button>
                </ModalFooter>
              </Modal>

              <Modal
                isOpen={showPopupLimit}
                centered
                backdrop
                toggle={this.togglePopupLimitClose}
                aria-labelledby={`${limitModalId}-title`}
                aria-describedby={`${limitModalId}-desc`}
              >
                <ModalHeader toggle={this.togglePopupLimitClose} id={`${limitModalId}-title`}>
                  <span className="sr-only">File Limit Error</span>
                </ModalHeader>
                <ModalBody>
                  <p id={`${limitModalId}-desc`} className="text-center text-danger">
                    <FormattedMessage id="global.label.attachmentLimitFileCount" />
                  </p>
                </ModalBody>
                <ModalFooter>
                  <Button outline color="danger" onClick={this.togglePopupLimitClose}>
                    <FormattedMessage id="global.label.popoverClose" />
                  </Button>
                </ModalFooter>
              </Modal>
            </section>
          )}
        </Dropzone>
      </div>
    )
  }
}

FileuploadInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.func]),
  onChange: PropTypes.func.isRequired,
  name: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  'aria-required': PropTypes.bool,
  'aria-invalid': PropTypes.bool,
  'aria-describedby': PropTypes.string,
}

FileuploadInput.defaultProps = {
  value: [],
}

export default FileuploadInput
